import { Link } from "react-router-dom"
import _ from "lodash"
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Avatar,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { ServiceIcon } from "../../icons"
import { IServiceConfig } from "providers/venue/types"
import { useMemo } from "react"
import { useVenue } from "providers/venue"
import { isValidURL } from "utils/url"

export interface IServiceCardProps extends AccordionProps {
  data: IServiceConfig
  sx?: SxProps
}

export const ServiceCard: React.FC<IServiceCardProps> = ({
  data,
  sx = {},
  ...props
}) => {
  const { t } = useTranslation()
  const { deviceType } = useVenue()

  const { locations = [], name, description, feature_image } = data
  const hasLocations = _.size(locations) > 0

  const theme = useTheme()
  const {
    root,
    accordionSummary,
    summaryContainer,
    titleText,
    descriptionText,
    serviceIconContainer,
    serviceIcon,
    infoContainer,
    accordionDetails,
    locationList,
    locationListItem,
    locationNavigateButton,
    locationTitleText,
    coverImage,
  } = theme.components[`ServiceCard`]?.styleOverrides || {}

  const expandMoreIcon = useMemo(
    () => getIcon(theme?.assets?.expandMoreIcon) || <ExpandMoreRoundedIcon />,
    [theme]
  )

  const arrowRightIcon = useMemo(
    () => getIcon(theme?.assets?.arrowRightIcon) || <ChevronRightRoundedIcon />,
    [theme]
  )
  return (
    <Accordion sx={deepmerge(root, sx)} {...props}>
      <AccordionSummary
        expandIcon={hasLocations && expandMoreIcon}
        sx={accordionSummary}
      >
        <Box sx={summaryContainer}>
          <Avatar sx={serviceIconContainer}>
            {getIcon(data, serviceIcon) || <ServiceIcon sx={serviceIcon} />}
          </Avatar>
          <Box sx={infoContainer}>
            <Typography variant="h4" sx={titleText}>
              {t(name)}
            </Typography>
            {!!description?.en && (
              <Typography variant="body1" sx={descriptionText}>
                {" "}
                {t(description)}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          sx={deepmerge(coverImage, {
            backgroundImage: `url(${feature_image})`,
          })}
        />
      </AccordionSummary>
      {hasLocations && (
        <AccordionDetails sx={accordionDetails}>
          <List disablePadding sx={locationList}>
            {locations.map((location, index) => {
              const path = location?.url

              /**
               * Location handling rules:
               * - External URLs: render an <a> tag via the `component` prop in LinkItem.
               * - Internal paths: use `Link` from react-router-dom.
               * - Internal links can open in a new tab only when `is_new_tab=true`; otherwise, no target is set.
               * - External links support both `target="_blank"` and `target="_self"` (page reloads on navigation).
               * - //! NOTE: kiosk cannot open external links.
               */
              const isExternalLink =
                location.type === "custom" && isValidURL(location?.url)
              const isNewTab = location?.is_new_tab
              const renderLinkComponent = isExternalLink ? "a" : Link
              const renderLinkProps = isExternalLink
                ? { href: path, target: isNewTab ? "_blank" : "_self" }
                : { to: path, ...(isNewTab ? { target: "_blank" } : {}) }
              const disabled = isExternalLink && deviceType !== "mobile"

              return (
                <ListItem
                  key={`location-item-${index}`}
                  component={renderLinkComponent}
                  sx={locationListItem}
                  {...(!disabled ? renderLinkProps : {})}
                  secondaryAction={
                    !disabled &&
                    path && (
                      <IconButton
                        edge="end"
                        aria-label="navigate"
                        sx={locationNavigateButton}
                      >
                        {arrowRightIcon}
                      </IconButton>
                    )
                  }
                >
                  <ListItemText
                    sx={locationTitleText}
                    primary={t(location?.label)}
                  />
                </ListItem>
              )
            })}
          </List>
        </AccordionDetails>
      )}
    </Accordion>
  )
}
